import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { Controller, useForm } from 'react-hook-form';

import {
  Button,
  FormControlLabel,
  IconButton,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
} from '@material-ui/core';

import CloseIcon from '@material-ui/icons/Close';
import { RootState } from 'state/store';
import { KeyboardDatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import timezone from 'dayjs/plugin/timezone';
import DateFnsUtils from '@date-io/date-fns';
import { DropDowns } from 'utils/constants';
import { formatDate } from 'utils/functions';

import { selectLeadCallCenterRoutingEnabled, selectLeadSmsCapable } from 'state/lead.state';

import { CommunicationTypes, ObjectLiteral, ScheduleFollowUpTimeFrames } from 'typings';
import { Autocomplete } from '@material-ui/lab';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);

const FormContainer = styled('form')`
  padding: ${(props) => props.theme.spacing(1, 2, 2)};
  position: relative;
  width: 450px;

  .form-close {
    position: absolute;
    top: ${(props) => props.theme.spacing(1)};
    right: ${(props) => props.theme.spacing(1)};
  }

  .form-row {
    display: flex;
    flex-direction: row;

    &:not(:last-child) {
      margin-bottom: 5px;
    }

    & > * {
      margin-left: 4px;
      margin-right: 4px;
    }
  }

  .form-type {
    .MuiFormControlLabel-root {
      margin-right: 25px;
    }
  }

  .form-datetime {
    .MuiFormControl-root,
    .MuiAutocomplete-root {
      &:first-child {
        flex: 2;
      }

      &:not(:first-child) {
        flex: 1;
      }
    }
  }

  .form-button {
    justify-content: flex-start;
    padding-top: 5px;
  }

  .small-icon {
    font-size: 1em;
  }

  .flex-row {
    display: flex;
    flex-direction: row;
  }
`;

const ScheduleFollowUpForm = ({
  onClose,
  onSubmit,
  timeFrame,
}: {
  onClose: Function;
  onSubmit: Function;
  timeFrame: ScheduleFollowUpTimeFrames;
}) => {
  const isSmsCapable = useSelector(selectLeadSmsCapable);
  const isCallConnectLead = useSelector(selectLeadCallCenterRoutingEnabled);

  const { engageByClientTimezone, agentTimezone, leadTimezone } = useSelector(
    (state: RootState) => ({
      engageByClientTimezone:
        state.lead.entity?.agent?.agentSetting?.settings?.lead?.engageByClientTimezone,
      agentTimezone: state.lead.entity?.agent?.agentSetting?.settings?.general?.timezone,
      leadTimezone: state.lead.entity?.timezone,
    })
  );

  // If the default timezone is going to be set to America/Phoenix then we need to replace the option for Mountain
  // time with that.
  const timezoneOptions = DropDowns.timezones;
  if (
    (engageByClientTimezone && agentTimezone === 'America/Phoenix') ||
    (!engageByClientTimezone && leadTimezone === 'America/Phoenix')
  ) {
    let mountainIndex = timezoneOptions.findIndex((item) => item.value === 'America/Denver');
    timezoneOptions[mountainIndex] = { label: 'Mountain', value: 'America/Phoenix' };
  }

  let defaultTimezone = '';
  if (engageByClientTimezone) {
    defaultTimezone = agentTimezone ?? 'America/Los_Angeles';
  } else {
    defaultTimezone = leadTimezone ?? 'America/Los_Angeles';
  }

  const [mode, setMode] = useState(isSmsCapable ? 'sms' : 'call');
  const [messageTimezone, setMessageTimezone] = useState(defaultTimezone);
  const [timeFrameError, setTimeFrameError] = useState('');

  const showFollowupMessageField = isSmsCapable && (!isCallConnectLead || mode === 'sms');

  let defaultDate = dayjs().tz(messageTimezone);

  // used by some of the time frames to ensure we don't overbook certain specific times
  const randomMinutes = Math.floor(Math.random() * 60);

  // determine the default date we're going to populate based on the time frame provided
  switch (timeFrame) {
    case ScheduleFollowUpTimeFrames.TOMORROW:
      defaultDate = defaultDate
        .add(1, 'day') // tomorrow
        .startOf('day') // midnight tomorrow
        .add(9, 'hours') // 9am tomorrow
        .add(randomMinutes, 'minutes');
      break;

    case ScheduleFollowUpTimeFrames.NEXT_WEEK:
      defaultDate = defaultDate
        .add(1, 'week') // next week
        .startOf('week') // Sunday of next week
        .add(1, 'day') // move to Monday
        .startOf('day') // midnight Monday; this might seem redundant, but DST changes can cause issues when based off of Sunday
        .add(9, 'hours') // 9am on Monday
        .add(randomMinutes, 'minutes');
      break;

    case ScheduleFollowUpTimeFrames.NEXT_MONTH:
      defaultDate = defaultDate
        .add(1, 'month') // next month
        .startOf('week') // Sunday of the week 1 month from now
        .add(1, 'day') // move to Monday
        .startOf('day') // midnight Monday; this might seem redundant, but DST changes can cause issues when based off of Sunday
        .add(9, 'hours') // 9am on Monday
        .add(randomMinutes, 'minutes');
      break;

    case ScheduleFollowUpTimeFrames.CUSTOM:
    default:
      // default to 1 minute from now to prevent accidentally scheduling in the past
      defaultDate = defaultDate.add(1, 'minute');
      break;
  }

  const {
    handleSubmit,
    control,
    getValues,
    setValue,
    errors,
    watch,
    setError,
    clearErrors,
    formState,
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      timezone: timezoneOptions.find((item) => item.value === defaultTimezone)
        ? defaultTimezone
        : '',
      followUpDate: formatDate(defaultDate.valueOf(), 'YYYY/MM/DD', messageTimezone.toString()),
      followUpTime: formatDate(defaultDate.valueOf(), 'h:mma', messageTimezone.toString()),
      followUpMessage: 'Hi there. Is now a good time to continue our conversation?',
      icon: mode,
    },
  });

  const saveFormData = handleSubmit((data) => {
    if (onSubmit) {
      let date = null;

      // validate the date and time combination before submitting
      try {
        date = dayjs.tz(
          `${data.followUpDate} ${data.followUpTime}`,
          'YYYY/MM/DD h:mma',
          messageTimezone
        );
      } catch (err) {
        setError('followUpDate', {
          type: 'manual',
          message: 'Invalid date or time',
        });
        return;
      }

      if (!date.isValid()) {
        setError('followUpDate', {
          type: 'manual',
          message: 'Invalid date or time',
        });
        return;
      }

      if (date.isBefore(dayjs())) {
        setTimeFrameError('Time cannot be in the past');
        return;
      }

      if (date.isAfter(dayjs().add(1, 'year'))) {
        setTimeFrameError('Time must be within the next year');
        return;
      }

      const payload: ObjectLiteral =
        mode === CommunicationTypes.CALL
          ? // the call vs SMS APIs expect different payloads;
            // should be resolved once both endpoints are in the same service
            {
              scheduledTime: date.unix(),
              timezone: data.timezone,
              followUpMessage: data.followUpMessage,
              icon: mode,
            }
          : {
              scheduledDate: date.format('YYYY/MM/DD hh:mm A'),
              timezone: data.timezone,
              text: data.followUpMessage,
              icon: mode,
            };

      onSubmit(payload);
    }
  });

  const handleModeChange = (event: any) => {
    setMode(event.target.value);
  };

  const handleTimezoneChange = (timezone: any) => {
    setMessageTimezone(timezone);
  };

  // when the date changes, validate and update the value in the form
  const handleDateChange = (value: any) => {
    try {
      const date = dayjs(value);

      if (date.isValid()) {
        setValue('followUpDate', date.format('YYYY/MM/DD'));
        clearErrors('followUpDate');
        setTimeFrameError('');
      } else {
        setError('followUpDate', {
          type: 'manual',
          message: 'Invalid date',
        });
      }
    } catch (err) {
      setError('followUpDate', {
        type: 'manual',
        message: 'Invalid date',
      });
    }
  };

  // when the time picker changes, update the value in the form
  let followUpTime = getValues('followUpTime');

  const handleTimeChange = (event: any, value: any) => {
    // material UI autocomplete sometimes returns an empty value when a value is actually set;
    // this is a workaround for that
    if (value) followUpTime = value;

    setValue('followUpTime', followUpTime);
  };

  // when the time picker closes, validate the time and reformat if necessary
  const handleTimeClose = (event: any) => {
    try {
      const date = dayjs(followUpTime, ['h:mma', 'ha', 'h:mm', 'h']);

      if (date.isValid()) {
        setValue('followUpTime', date.format('h:mma'));
        clearErrors('followUpTime');
        setTimeFrameError('');
      } else {
        setError('followUpTime', {
          type: 'manual',
          message: 'Invalid time',
        });
      }
    } catch (err) {
      setError('followUpTime', {
        type: 'manual',
        message: 'Invalid time',
      });
    }
  };

  const getTimeOptionLabel = (option: any): string => {
    // material UI autocomplete sometimes returns an empty value when a value is actually set;
    // this is a workaround for that
    if (!option) return '';

    // if the option was typed in rather than selected from the autocomplete, it will be a string
    return typeof option === 'string' ? option : option.label;
  };

  // determine the selected option to highlight based on the value in the form
  const getTimeOptionSelected = (option: any, value: any): boolean => {
    try {
      const date = dayjs(value, ['h:mma', 'h:mm', 'h']);

      // highlight the closest option to the value in the form, rounded down to the nearest 15 minutes
      return (
        option.hour === date.hour() &&
        option.minute <= date.minute() &&
        option.minute > date.minute() - 15
      );
    } catch (err) {
      return false;
    }
  };

  const generateTimeOptions = (): any[] => {
    const options = [];

    for (let i = 0; i < 24; i++) {
      const amPm = i < 12 ? 'am' : 'pm';
      const hour = i % 12 || 12;

      options.push({ hour: i, minute: 0, label: `${hour}:00${amPm}` });
      options.push({ hour: i, minute: 15, label: `${hour}:15${amPm}` });
      options.push({ hour: i, minute: 30, label: `${hour}:30${amPm}` });
      options.push({ hour: i, minute: 45, label: `${hour}:45${amPm}` });
    }

    return options;
  };

  const timeOptions = generateTimeOptions();

  return (
    <FormContainer onSubmit={saveFormData}>
      <IconButton className="no-padding form-close" aria-label="cancel" onClick={() => onClose()}>
        <CloseIcon />
      </IconButton>
      <div className="form-row form-type">
        <RadioGroup
          aria-label="follow-up type"
          name="followup-type"
          value={mode}
          onChange={handleModeChange}
          row
        >
          <FormControlLabel value="call" control={<Radio size="small" />} label="call" />
          {isSmsCapable && <FormControlLabel value="sms" control={<Radio />} label="sms" />}
        </RadioGroup>
      </div>
      <div className="form-row form-datetime">
        <Controller
          name="followUpDate"
          control={control}
          render={(props) => (
            // Had to use DateFnsUtils instead of the dayjs ones due to a bug with the material UI picker.
            // Consider checking in the future to see if it gets fixed.
            <MuiPickersUtilsProvider utils={DateFnsUtils}>
              <KeyboardDatePicker
                autoOk
                disableToolbar
                variant="inline"
                name="followUpDateInput"
                label="date"
                value={watch('followUpDate')}
                onChange={handleDateChange}
                autoFocus
                disablePast
                format="yyyy/MM/dd"
                error={!!errors.followUpDate || !!timeFrameError}
                helperText={errors.followUpDate?.message || timeFrameError}
                inputProps={{
                  'data-testid': 'pickerTestId',
                }}
              />
            </MuiPickersUtilsProvider>
          )}
        ></Controller>
        <Controller
          as={
            <Autocomplete
              freeSolo
              options={timeOptions}
              disableClearable
              filterOptions={(options, state) => options} // always show all options
              getOptionLabel={getTimeOptionLabel}
              getOptionSelected={getTimeOptionSelected}
              onInputChange={handleTimeChange}
              onClose={handleTimeClose}
              renderOption={(option) => option.label}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="time"
                  name="followUpTimeInput"
                  error={!!errors.followUpTime}
                  helperText={errors.followUpTime?.message}
                  required
                />
              )}
            />
          }
          name="followUpTime"
          control={control}
          label="time"
        ></Controller>
        <Controller
          as={<TextField select required />}
          name="timezone"
          control={control}
          label="timezone"
          rules={{ required: 'A timezone is required' }}
          error={!!errors.timezone}
          helperText={errors.timezone?.message}
        >
          <MenuItem value="">
            <i>Select</i>
          </MenuItem>
          {timezoneOptions.map((timezone) => (
            <MenuItem
              key={timezone.label}
              value={timezone.value}
              onClick={() => handleTimezoneChange(timezone.value)}
            >
              {timezone.label}
            </MenuItem>
          ))}
        </Controller>
      </div>
      <div className="form-row">
        {showFollowupMessageField && (
          <Controller
            as={<TextField multiline />}
            name="followUpMessage"
            control={control}
            rules={{
              maxLength: {
                value: 1600,
                message:
                  'Message too long: SMS messages must be 1600 characters or fewer. Please shorten your message before scheduling',
              },
            }}
            error={!!errors.followUpMessage}
            fullWidth
            maxRows={2}
            label="follow-up message"
            helperText={errors.followUpMessage?.message}
          />
        )}
      </div>
      <div className="form-row form-button">
        <Button type="submit" variant="contained" color="primary" disabled={!formState.isValid}>
          Save
        </Button>
      </div>
    </FormContainer>
  );
};

export default ScheduleFollowUpForm;
